// =============================================================================
// MeuDriver — Batch U3: Hook de tags de especialidade do instrutor
// =============================================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { toast } from 'sonner'
import type { InstructorTagDefinitionRow } from '@/types/database'

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const tagKeys = {
  all: ['instructor-tags'] as const,
  definitions: () => [...tagKeys.all, 'definitions'] as const,
  byInstructor: (id: string) => [...tagKeys.all, 'instructor', id] as const,
}

// ---------------------------------------------------------------------------
// useAllTagDefinitions — catálogo público de tags
// ---------------------------------------------------------------------------

export function useAllTagDefinitions() {
  return useQuery<InstructorTagDefinitionRow[]>({
    queryKey: tagKeys.definitions(),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('instructor_tag_definitions')
        .select('*')
        .eq('is_active', true)
        .order('category')
        .order('label')
      if (error) throw error
      return (data ?? []) as InstructorTagDefinitionRow[]
    },
    staleTime: 1000 * 60 * 30, // catálogo muda raramente
  })
}

// ---------------------------------------------------------------------------
// useInstructorTags — tags de um instrutor específico
// ---------------------------------------------------------------------------

export function useInstructorTags(instructorId: string) {
  return useQuery<string[]>({
    queryKey: tagKeys.byInstructor(instructorId),
    enabled: !!instructorId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('instructor_tags')
        .select('tag_id')
        .eq('instructor_id', instructorId)
      if (error) throw error
      return (data ?? []).map((r) => r.tag_id as string)
    },
    staleTime: 1000 * 60 * 5,
  })
}

// ---------------------------------------------------------------------------
// useUpdateInstructorTags — delete + insert atômico via transação client-side
// ---------------------------------------------------------------------------

export function useUpdateInstructorTags(instructorId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (tagIds: string[]) => {
      if (tagIds.length > 8) {
        throw new Error('Máximo de 8 tags por instrutor')
      }

      // Delete todas as tags atuais
      const { error: delErr } = await supabase
        .from('instructor_tags')
        .delete()
        .eq('instructor_id', instructorId)
      if (delErr) throw delErr

      // Insert das selecionadas (se houver)
      if (tagIds.length > 0) {
        const rows = tagIds.map((tag_id) => ({ instructor_id: instructorId, tag_id }))
        const { error: insErr } = await supabase
          .from('instructor_tags')
          .insert(rows)
        if (insErr) throw insErr
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tagKeys.byInstructor(instructorId) })
      toast.success('Especialidades atualizadas!')
    },
    onError: (err: Error) => {
      toast.error('Erro ao salvar especialidades: ' + err.message)
    },
  })
}
